import { CaseStatusType, CaseType, User } from '../../types';

export type CaseStateTone = 'default' | 'info' | 'success' | 'warning' | 'error';

const TONES: Record<string, CaseStateTone> = {
    APPROVED: 'success',
    DENIED: 'error',
    ESCALATED: 'warning',
    ESCALATION_RESPONSE: 'warning',
    INFO_REQUESTED: 'warning',
    IN_REVIEW: 'info',
    SUBMITTED: 'info',
    UNSUBMITTED: 'default',
};

export type ReviewDecision = { value: string; label: string; description: string; healerVisible: boolean };

// Mirrors heal.services.case_review and HealCaseStatusSerializer on the backend.
export const REVIEW_DECISIONS: Record<string, ReviewDecision> = {
    APPROVED: { value: 'APPROVED', label: 'Approve', description: 'The case counts towards the healer\'s course requirements.', healerVisible: true },
    DENIED: { value: 'DENIED', label: 'Deny', description: 'The case does not meet the requirements.', healerVisible: true },
    INFO_REQUESTED: { value: 'INFO_REQUESTED', label: 'Request information', description: 'The healer must add detail before a decision.', healerVisible: true },
    ESCALATED: { value: 'ESCALATED', label: 'Escalate', description: 'Ask another reviewer for a second opinion.', healerVisible: false },
    ESCALATION_RESPONSE: { value: 'ESCALATION_RESPONSE', label: 'Respond to escalation', description: 'Give your opinion so a reviewer can make the final decision.', healerVisible: false },
};

const OWNER_DECISIONS = ['APPROVED', 'DENIED', 'INFO_REQUESTED', 'ESCALATED'];
const CLAIMABLE_STATES = ['SUBMITTED', 'ESCALATION_RESPONSE'];

export type CaseStateOption = { value: string; label: string; healerSees: string; notesVisibleToHealer: boolean };

// Mirrors HealCaseStatusSerializer: these three are shown to healers as "Submitted" with no notes.
export const CASE_STATE_OPTIONS: CaseStateOption[] = [
    { value: 'SUBMITTED', label: 'Submitted', healerSees: 'Submitted', notesVisibleToHealer: true },
    { value: 'IN_REVIEW', label: 'In review', healerSees: 'Submitted', notesVisibleToHealer: false },
    { value: 'INFO_REQUESTED', label: 'Information requested', healerSees: 'Information requested', notesVisibleToHealer: true },
    { value: 'APPROVED', label: 'Approved', healerSees: 'Approved', notesVisibleToHealer: true },
    { value: 'DENIED', label: 'Denied', healerSees: 'Denied', notesVisibleToHealer: true },
    { value: 'ESCALATED', label: 'Escalated', healerSees: 'Submitted', notesVisibleToHealer: false },
    { value: 'ESCALATION_RESPONSE', label: 'Escalation response', healerSees: 'Submitted', notesVisibleToHealer: false },
    { value: 'UNSUBMITTED', label: 'Unsubmitted', healerSees: 'Unsubmitted', notesVisibleToHealer: true },
];

// The review may still continue from these, so the reviewer's draft is kept.
export const clearsReviewDraft = (state: string) => !['IN_REVIEW', 'SUBMITTED'].includes(state);

const timeOf = (value?: string) => {
    const time = value ? Date.parse(value) : NaN;
    return Number.isFinite(time) ? time : null;
};

// Real time first, then the higher id; unreadable dates sort last.
export const compareStatusesNewestFirst = (a: CaseStatusType, b: CaseStatusType) => {
    const timeA = timeOf(a.created_date);
    const timeB = timeOf(b.created_date);
    if (timeA !== timeB) {
        if (timeA === null) return 1;
        if (timeB === null) return -1;
        return timeB - timeA;
    }
    return b.id - a.id;
};

export const getLatestStatus = (reviewCase: CaseType): CaseStatusType | undefined =>
    [...(reviewCase.statuses ?? [])].sort(compareStatusesNewestFirst)[0];

export const getLatestCaseState = (reviewCase: CaseType): string => getLatestStatus(reviewCase)?.state ?? 'NONE';

const setterOf = (status?: CaseStatusType) =>
    status?.setter && typeof status.setter === 'object' ? status.setter as User : undefined;

const setterIdOf = (status?: CaseStatusType) =>
    typeof status?.setter === 'number' ? status.setter : setterOf(status)?.id;

export type ReviewAccess = {
    state: string;
    latestStatusId?: number;
    claimedAt?: string;
    holderName?: string;
    isMine: boolean;
    claimedByOther: boolean;
    canClaim: boolean;
    decisions: ReviewDecision[];
};

export const getReviewAccess = (reviewCase: CaseType, reviewerId: number): ReviewAccess => {
    const latest = getLatestStatus(reviewCase);
    const state = latest?.state ?? 'NONE';
    const claimed = state === 'IN_REVIEW';
    const isMine = claimed && setterIdOf(latest) === reviewerId;
    const decisionKeys = isMine ? OWNER_DECISIONS
        : state === 'ESCALATED' ? ['ESCALATION_RESPONSE']
            : state === 'INFO_REQUESTED' ? ['APPROVED']
                : [];
    return {
        state,
        latestStatusId: latest?.id,
        claimedAt: claimed ? latest?.created_date : undefined,
        holderName: claimed ? setterOf(latest)?.full_name || 'Another reviewer' : undefined,
        isMine,
        claimedByOther: claimed && !isMine,
        canClaim: CLAIMABLE_STATES.includes(state),
        decisions: decisionKeys.map(key => REVIEW_DECISIONS[key]),
    };
};

export const formatCaseState = (state: string) => state.replace(/_/g, ' ').toLowerCase();

export const caseStateTone = (state: string): CaseStateTone => TONES[state] ?? 'default';
